import React from 'react';
import { Modal, Box, Typography, Paper, Stack, Chip, ChipProps, IconButton, useTheme, Alert } from '@mui/material';
import { YEARS_TO_MATURITY } from '../../create-portfolio';
import { PortfolioYear } from '../../types/syndication';
import { formatCurrency } from '../../utils';
import { calculateDepreciationForYear } from '../../utils/syndication';
import CloseIcon from '@mui/icons-material/Close';

const EVENT_TYPE_ACQUISITION = 'acquisition';
const EVENT_TYPE_DISPOSITION = 'disposition';
const EVENT_TYPE_DISTRIBUTION = 'distribution';
const EVENT_TYPE_PASSIVE_INCOME = 'passiveIncome';
const EVENT_TYPE_LAST_YEAR_TAXES = 'lastYearTaxes';
const EVENT_TYPE_REINVESTED_DISTRIBUTIONS = 'reinvestedDistributions';
type EventType = typeof EVENT_TYPE_ACQUISITION | typeof EVENT_TYPE_DISPOSITION | typeof EVENT_TYPE_DISTRIBUTION | typeof EVENT_TYPE_PASSIVE_INCOME | typeof EVENT_TYPE_LAST_YEAR_TAXES | typeof EVENT_TYPE_REINVESTED_DISTRIBUTIONS;

const EventChip = ({ type }: { type: EventType }) => {
    const chipProps: ChipProps = {
        color: 'primary',
        sx: {
            marginRight: 1,
        }
    }

    switch (type) {
        case EVENT_TYPE_ACQUISITION:
            return <Chip label="Bought" {...chipProps} />;
        case EVENT_TYPE_DISPOSITION:
            return <Chip label="Sold" {...chipProps} />;
        case EVENT_TYPE_DISTRIBUTION:
            return <Chip label="Distributions" {...chipProps} color="secondary" />;
        case EVENT_TYPE_PASSIVE_INCOME:
            return <Chip label="Passive Income" {...chipProps} color="secondary" />;
        case EVENT_TYPE_LAST_YEAR_TAXES:
            return <Chip label="Taxes" {...chipProps} color="warning" />;
        case EVENT_TYPE_REINVESTED_DISTRIBUTIONS:
            return <Chip label="Reinvested" {...chipProps} color="primary" />;
        default:
            return null;
    }
}

const EventListItem = ({ children }: { children: React.ReactNode }) => {
    return (
        <li style={{ marginBottom: 10 }}>
            {children}
        </li>
    )
}

interface YearModalProps {
    open: boolean;
    onClose: () => void;
    yearData: PortfolioYear;
    lastYearData?: PortfolioYear;
    equityMultiple: number;
    cocReturn: number;
    includeTaxes: boolean;
}

const YearModal: React.FC<YearModalProps> = ({ open, onClose, yearData, lastYearData, equityMultiple, cocReturn, includeTaxes }) => {
    const theme = useTheme();
    const { year, syndications, distributions, passiveIncome, reinvested } = yearData;
    const { taxes: lastYearTaxes } = lastYearData || {};
    const lastYearTaxesTotal = lastYearTaxes?.getTotalSyndicationTaxes() || 0;

    // Calculate appreciation rate
    const AAR = (equityMultiple - 1) / YEARS_TO_MATURITY;
    const appreciation = (AAR - cocReturn) * YEARS_TO_MATURITY;

    // Calculate syndications sold this year (matured in previous year)
    const soldSyndications = lastYearData
        ? lastYearData.syndications.filter(syn => syn.startYear + YEARS_TO_MATURITY === year)
        : syndications.filter(syn => syn.startYear + YEARS_TO_MATURITY === year);
    const totalSoldAmount = soldSyndications.reduce((sum, syn) => sum + (syn.amountInvested * (1 + appreciation)), 0);

    // Calculate syndications bought this year
    const boughtSyndications = syndications.filter(syn => syn.startYear === year);
    const totalBoughtAmount = boughtSyndications.reduce((sum, syn) => sum + syn.amountInvested, 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="year-modal-title"
        >
            <Box sx={{
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'primary',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60%',
                maxWidth: 600,
                maxHeight: '90vh',
                overflowY: 'auto',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography id="year-modal-title" variant="h6" component="h2">
                        {year} Summary
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon sx={{ color: theme.palette.grey[600] }} />
                    </IconButton>
                </Box>

                <Stack spacing={3}>
                    {/* Syndication Activity */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Syndication Activity
                        </Typography>
                        {soldSyndications.length === 0 && boughtSyndications.length === 0 ? (
                            <Typography color="text.secondary">No syndication activity this year</Typography>
                        ) : (
                            <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyleType: 'none' }}>
                                {soldSyndications.length > 0 && (
                                    <EventListItem>
                                        <EventChip type={EVENT_TYPE_DISPOSITION} />
                                        <Typography component="span">
                                            Sold {soldSyndications.length} syndication(s) for {formatCurrency(totalSoldAmount)}
                                        </Typography>
                                    </EventListItem>
                                )}
                                {boughtSyndications.length > 0 && (
                                    <EventListItem>
                                        <EventChip type={EVENT_TYPE_ACQUISITION} />
                                        <Typography component="span">
                                            Purchased {boughtSyndications.length} syndication(s) for {formatCurrency(totalBoughtAmount)}
                                        </Typography>
                                    </EventListItem>
                                )}
                                {/* Info about reinvesting from last year */}
                                {yearData.reinvestFromLastYear > 0 && (
                                    <Alert severity="info">
                                        {soldSyndications.length > 0 ? (
                                            <>
                                                The deal this year was bought with {formatCurrency(yearData.reinvestFromLastYear)} of distributions
                                                from last year in addition to {formatCurrency(totalSoldAmount)} from this year's disposition.
                                            </>
                                        ) : (
                                            <>
                                                The deal this year was also bought with {formatCurrency(yearData.reinvestFromLastYear)} of distributions
                                                from last year.
                                            </>
                                        )}
                                    </Alert>

                                )}
                            </ul>
                        )}
                    </Paper>

                    {/* Income Summary */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Income Summary
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyleType: 'none' }}>
                            <EventListItem>
                                <EventChip type={EVENT_TYPE_DISTRIBUTION} />
                                <Typography component="span">
                                    Received {formatCurrency(distributions || 0)}
                                </Typography>
                            </EventListItem>
                            {lastYearTaxesTotal > 0 && (
                                <EventListItem>
                                    <EventChip type={EVENT_TYPE_LAST_YEAR_TAXES} />
                                    <Typography component="span">
                                        Taxes paid from last year: {formatCurrency(lastYearTaxesTotal)}
                                    </Typography>
                                </EventListItem>
                            )}
                            {reinvested ? (
                                <EventListItem>
                                    <EventChip type={EVENT_TYPE_REINVESTED_DISTRIBUTIONS} />
                                    <Typography component="span">
                                        {formatCurrency((distributions ?? 0) - lastYearTaxesTotal)}
                                    </Typography>
                                </EventListItem>
                            ) : (
                                <EventListItem>
                                    <EventChip type={EVENT_TYPE_PASSIVE_INCOME} />
                                    <Typography component="span">
                                        Passive Income: {formatCurrency(passiveIncome)}
                                    </Typography>
                                </EventListItem>
                            )}
                        </ul>
                    </Paper>
                    {includeTaxes && (
                        <>
                            {/* Tax Summary */}
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Tax Summary
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyleType: 'none' }}>
                                    <EventListItem>
                                        <Typography component="span">
                                            Depreciation Taken: {formatCurrency(syndications.reduce((sum, syn) => sum + calculateDepreciationForYear(year - syn.startYear, syn.amountInvested), 0))}
                                        </Typography>
                                    </EventListItem>
                                    <EventListItem>
                                        <Typography component="span">
                                            Passive Income Tax: {formatCurrency(yearData.taxes.ordinaryPassiveIncome)}
                                        </Typography>
                                    </EventListItem>
                                    <EventListItem>
                                        <Typography component="span">
                                            Capital Gains Tax: {formatCurrency(yearData.taxes.ltCapitalGains)}
                                        </Typography>
                                    </EventListItem>
                                    <EventListItem>
                                        <Typography component="span">
                                            Depreciation Recapture: {formatCurrency(yearData.taxes.depreciationRecapture)}
                                        </Typography>
                                    </EventListItem>
                                    <EventListItem>
                                        <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                            Total Tax Liability: {formatCurrency(yearData.taxes.getTotalSyndicationTaxes())}
                                        </Typography>
                                    </EventListItem>
                                </ul>
                            </Paper>
                        </>
                    )}
                </Stack>
            </Box>
        </Modal >
    );
};

export default YearModal;
